import { useEffect, useState } from 'react';
import queryString from 'query-string';
import { Endpoints } from 'UI/constants/endpoints';

import API from './API';
import { sendRequest } from './utils';

const title = 'Placements';

/**
 * @param {number} id
 * @returns {Promise<OperationResult>}
 */
export const getChannelPartnersByUserId = async id =>
  sendRequest(`${Endpoints.Users}/${id}/${Endpoints.ChannelPartner}`, 'get');

/**
 * @param {number} id
 * @param {string[]} [scopes]
 * @param {string[]} [relations]
 * @returns {Promise<OperationResult>}
 */
export const getPlacementById = async (id, scopes = [], relations = []) => {
  const params = {
    scopes: scopes || [],
    relations: relations || []
  };
  const queryParams = queryString.stringify(params, { arrayFormat: 'comma' });
  const result = await sendRequest(`${Endpoints.Placements}/${id}?${queryParams}`, 'get');

  return result;
};

/**
 * @param {*} data
 * @returns {Promise<OperationResult>}
 */
export const createPlacement = async data =>
  sendRequest(
    Endpoints.Placements,
    'post',
    data,
    `Waiting for Regional Director approval`,
    'Placement registered successfully'
  );

/**
 * @param {number} id
 * @param {*} data
 * @param {*} options
 * @returns {Promise<OperationResult>}
 */
export const updatePlacement = async (id, data, options) =>
  sendRequest(
    `${Endpoints.Placements}/${id}`,
    'put',
    data,
    options?.successBody || 'The placement was updated successfully',
    options?.successTitle || title
  );

/**
 * @param {number} id
 * @param {*} data
 * @returns {Promise<OperationResult>}
 */
export const createSuggestion = async (id, data) =>
  sendRequest(
    `${Endpoints.PlacementSuggestions.replace(':id', id)}`,
    'post',
    data,
    'The change request was created successfully',
    title
  );

/**
 * @param {number} id
 * @param {*} data
 * @returns {Promise<OperationResult>}
 */
export const createInvoice = async (id, data) =>
  sendRequest(
    `${Endpoints.PlacementInvoices.replace(':id', id)}`,
    'post',
    data,
    'The invoice was added successfully',
    title
  );

/**
 * @param {number} placementId
 * @param {number} invoiceId
 * @param {*} data
 * @returns {Promise<OperationResult>}
 */
export const updateInvoice = async (placementId, invoiceId, data) =>
  sendRequest(
    `${Endpoints.PlacementInvoices.replace(':id', placementId)}/${invoiceId}`,
    'patch',
    data,
    'The invoice was updated successfully',
    title
  );

/**
 * @param {number} id
 * @returns {Promise<OperationResult>}
 */
export const getInvoices = async id =>
  sendRequest(`${Endpoints.PlacementInvoices.replace(':id', id)}`, 'get');

/**
 * @param {number} placementId
 * @param {number} invoiceId
 * @returns {Promise<OperationResult>}
 */
export const deleteInvoice = async (placementId, invoiceId) =>
  sendRequest(`${Endpoints.PlacementInvoices.replace(':id', placementId)}/${invoiceId}`, 'delete');

/**
 * @param {number} id
 * @param {*} data
 * @returns {Promise<OperationResult>}
 */
export const createPayment = async (id, data) =>
  sendRequest(
    `${Endpoints.PlacementPayments.replace(':id', id)}`,
    'post',
    data,
    'The payment was added successfully',
    title
  );

/**
 * @param {number} placementId
 * @param {number} paymentId
 * @param {*} data
 * @returns {Promise<OperationResult>}
 */
export const updatePayment = async (placementId, paymentId, data) =>
  sendRequest(
    `${Endpoints.PlacementPayments.replace(':id', placementId)}/${paymentId}`,
    'patch',
    data,
    'The payment was updated successfully',
    title
  );

/**
 * @param {number} placementId
 * @param {number} paymentId
 * @returns {Promise<OperationResult>}
 */
export const deletePayment = async (placementId, paymentId) =>
  sendRequest(`${Endpoints.PlacementPayments.replace(':id', placementId)}/${paymentId}`, 'delete');

/**
 * @param {*} data
 * @param {*} options
 * @returns {Promise<OperationResult>}
 */
export const putRequest = async (data, options) =>
  sendRequest(
    `${options.endpoint}`,
    'put',
    data,
    options.successBody || 'Your request was submitted successfully',
    options.successTitle || title
  );

/**
 * @param {number} id
 * @param {*} data
 * @param {*} options
 * @returns {Promise<OperationResult>}
 */
export const requestFallOff = async (id, data, options) =>
  putRequest(data, {
    ...options,
    endpoint: `${Endpoints.PlacementRequestMarkFallOff.replace(':id', id)}`
  });

/**
 * @param {number} id
 * @param {*} data
 * @param {*} options
 * @returns {Promise<OperationResult>}
 */
export const requestRevertFallOff = async (id, data, options) =>
  putRequest(data, {
    ...options,
    endpoint: `${Endpoints.PlacementRequestRevertFallOff.replace(':id', id)}`
  });

/**
 * @param {number} id
 * @param {*} data
 * @param {*} options
 * @returns {Promise<OperationResult>}
 */
export const markAsFallOff = async (id, data, options) =>
  sendRequest(
    `${Endpoints.PlacementMarkFallOff.replace(':id', id)}`,
    'put',
    data,
    options?.successBody || 'The placement was marked as fall off successfully',
    options?.successTitle || title
  );

/**
 * @param {number} id
 * @param {*} data
 * @param {*} options
 * @returns {Promise<OperationResult>}
 */
export const revertFallOff = async (id, data, options) =>
  sendRequest(
    `${Endpoints.PlacementRevertFallOff.replace(':id', id)}`,
    'put',
    data,
    options?.successBody || 'The fall off was reverted successfully',
    options?.successTitle || title
  );

/**
 * @param {number} id
 * @param {*} options
 * @returns {Promise<OperationResult>}
 */
export const requestZip = async (id, options) =>
  sendRequest(
    `${Endpoints.PlacementRequestZip.replace(':id', id)}`,
    'post',
    null,
    options?.successBody || `You'll receive an email in a few seconds with a download link`,
    options?.successTitle || title
  );

/**
 * @param {number} id
 * @param {*} data
 * @returns {Promise<OperationResult>}
 */
export const saveEstimate = async (id, data) =>
  sendRequest(
    `${Endpoints.PlacementEstimate.replace(':id', id)}`,
    'put',
    data,
    'The estimate was saved successfully',
    title
  );

/**
 * @param {number} id
 * @returns {Promise<OperationResult>}
 */
export const approveEstimate = async id =>
  sendRequest(
    `${Endpoints.PlacementApproveEstimate.replace(':id', id)}`,
    'put',
    null,
    'The estimate was approved successfully',
    title
  );

/**
 * @param {string} module
 * @returns {AttachmentCategory[]}
 */
export const useFetchFileCategories = module => {
  const [fileCategories, setFileCategories] = useState([]);

  useEffect(() => {
    (async () => {
      const response = await API.get(`${Endpoints.FileCategories}?module=${module}`);
      response.status === 200 && setFileCategories(response.data);
    })();
  }, [module]);

  return fileCategories;
};

/**
 * @param {number[]} [roles]
 */
export const useFetchUsers = (roles = []) => {
  const [users, setUsers] = useState([]);
  const rolesParam = roles.join(',');

  useEffect(() => {
    (async () => {
      const response = await API.get(`${Endpoints.Users}?role_id=${rolesParam}`);
      response.status === 200 && setUsers(response.data);
    })();
  }, [rolesParam]);

  return users;
};

/**
 * @param {number} id
 * @param {*} [data]
 * @returns {Promise<OperationResult>}
 */
export const recreateEstimate = async (id, data = null, options) =>
  sendRequest(
    `${Endpoints.PlacementRecreateEstimate.replace(':id', id)}`,
    'put',
    data,
    options?.successBody,
    options?.successTitle || title
  );

/**
 * @param {number} id
 * @returns {Promise<OperationResult>}
 */
export const deletePlacement = async (id, options) =>
  sendRequest(
    `${Endpoints.PlacementDeletePermanently.replace(':id', id)}`,
    'delete',
    {},
    options?.successBody,
    options?.successTitle || title
  );
